/**
 * Badges for the Events pages: source (with a link to the source), review, status, sync run and
 * mapping status and suggestion confidence; Stage 1B link origin, role and link type.
 */

import { IconExternalLink } from '@tabler/icons-react';
import { Anchor, Group } from '@/components/ui';
import { EventSourceIds } from '@actschurch/ccms-sdk';
import { Tag } from './EventsUi';
import { LINK_TYPE_COLORS, LINK_TYPE_NAMES, ORIGIN_COLORS, ORIGIN_NAMES, ROLE_COLORS, ROLE_NAMES } from './linkUtils';
import {
  MAPPING_STATUS_COLORS,
  MAPPING_STATUS_NAMES,
  REVIEW_COLORS,
  REVIEW_NAMES,
  RUN_STATUS_COLORS,
  RUN_STATUS_NAMES,
  SOURCE_COLORS,
  STATUS_COLORS,
  STATUS_NAMES,
  sourceName,
} from './eventsUtils';

export function SourceBadge({
  sourceID,
  name,
  url,
  detached,
  withLink = false,
}: {
  sourceID?: number | null;
  name?: string | null;
  url?: string | null;
  detached?: boolean;
  withLink?: boolean;
}) {
  const id = sourceID ?? EventSourceIds.Ccms;
  const label = sourceName(id, name);
  const badge = (
    <Tag
      color={SOURCE_COLORS[id] ?? 'gray'}
      tooltip={detached ? `Detached from ${label}. Changes there no longer reach this event.` : undefined}
      variant={detached ? 'outline' : 'light'}
    >
      {detached ? `${label} (detached)` : label}
    </Tag>
  );
  if (!withLink || !url) return badge;
  return (
    <Group gap={4} wrap="nowrap">
      {badge}
      <Anchor href={url} target="_blank" rel="noopener noreferrer" size="xs">
        <Group gap={2} wrap="nowrap">
          <IconExternalLink size={12} />
          Open
        </Group>
      </Anchor>
    </Group>
  );
}

export function ReviewBadge({ reviewStatusID, name }: { reviewStatusID?: number | null; name?: string | null }) {
  const id = reviewStatusID ?? 1;
  return <Tag color={REVIEW_COLORS[id] ?? 'gray'}>{name || REVIEW_NAMES[id] || 'Unknown'}</Tag>;
}

export function StatusBadge({ statusID, name }: { statusID: number; name?: string | null }) {
  return <Tag color={STATUS_COLORS[statusID] ?? 'gray'}>{name || STATUS_NAMES[statusID] || `Status ${statusID}`}</Tag>;
}

export function RunStatusBadge({ statusID, name }: { statusID?: number | null; name?: string | null }) {
  if (statusID == null) return <Tag>No runs yet</Tag>;
  return <Tag color={RUN_STATUS_COLORS[statusID] ?? 'gray'}>{name || RUN_STATUS_NAMES[statusID] || 'Unknown'}</Tag>;
}

export function MappingStatusBadge({ statusID, name }: { statusID: number; name?: string | null }) {
  return <Tag color={MAPPING_STATUS_COLORS[statusID] ?? 'gray'}>{name || MAPPING_STATUS_NAMES[statusID] || 'Unknown'}</Tag>;
}

export function ConfidenceBadge({ confidence }: { confidence: number }) {
  const value = Math.max(0, Math.min(100, Math.round(confidence)));
  const color = value >= 80 ? 'teal' : value >= 50 ? 'yellow' : 'gray';
  return <Tag color={color}>{value}% match</Tag>;
}

/** Where a link came from: Rule, Learned rule, AI, Source tag or Manual. The tooltip says why. */
export function OriginBadge({ originID, name, why }: { originID: number; name?: string | null; why?: string | null }) {
  return (
    <Tag color={ORIGIN_COLORS[originID] ?? 'gray'} variant="outline" tooltip={why || undefined}>
      {name || ORIGIN_NAMES[originID] || 'Unknown'}
    </Tag>
  );
}

export function RoleBadge({ roleID, name }: { roleID?: number | null; name?: string | null }) {
  if (roleID == null) return null;
  return <Tag color={ROLE_COLORS[roleID] ?? 'gray'}>{name || ROLE_NAMES[roleID] || 'Involved'}</Tag>;
}

export function LinkTypeBadge({ linkTypeID, name }: { linkTypeID: number; name?: string | null }) {
  return (
    <Tag color={LINK_TYPE_COLORS[linkTypeID] ?? 'gray'} variant="light">
      {LINK_TYPE_NAMES[linkTypeID] || name || 'Link'}
    </Tag>
  );
}
